/**
 * Data access layer. Two adapters behind one interface:
 *   - MemoryRepository  — runs with zero backend config (seed from shared/content)
 *   - SupabaseRepository — production (RLS enforces the same rules server-side)
 *
 * All writes go through `saveArticle`, which validates with the shared schema and
 * guarantees a unique, URL-safe slug. There is no other write path.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { resolveSlug } from '../shared/slug.js';
import { validate, articleSchema, categorySchema } from '../shared/schema.js';
import { isPubliclyVisible } from '../shared/status.js';
import * as seed from '../shared/content.js';
// Circular by design: pgRepo imports this module's types and error classes,
// but only touches them inside method bodies, so the bindings are live by then.
import { HttpRepository } from './pgRepo';

export type Role = 'admin' | 'editor' | 'writer' | 'analyst' | 'social_manager' | 'seo_manager';

/**
 * The same set as `Role`, at runtime.
 *
 * Needed because a response is data, not a type: the API contract says
 * `roles: Role[]`, but a union cannot validate a string that arrives over the
 * wire. Letting an unrecognised role through would be worse than dropping it —
 * anything that checks a permission should fail closed. Annotating this as
 * `readonly Role[]` means a typo here is a compile error.
 */
export const ROLES: readonly Role[] = [
  'admin',
  'editor',
  'writer',
  'analyst',
  'social_manager',
  'seo_manager',
];
export type ArticleStatus = 'draft' | 'in_review' | 'approved' | 'scheduled' | 'published' | 'archived';

export interface Author { id: string; name: string; slug: string; bio: string; avatarUrl: string }
export interface Category { id: string; name: string; slug: string; description: string; sortOrder: number }
export interface SeoFields { title: string; description: string; canonical: string; ogImage: string; noindex: boolean }
export interface Article {
  id: string;
  title: string;
  slug: string;
  dek: string;
  body: string;
  coverImageUrl: string;
  categoryId: string;
  authorId: string;
  languageCode: string;
  regionCode: string;
  tags: string[];
  status: ArticleStatus;
  scheduledFor: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  revision: number;
  viewCount: number;
  seo: SeoFields;
  category?: Category | null;
  author?: Author | null;
}
export interface MediaItem {
  id: string; fileName: string; url: string; mimeType: string; sizeBytes: number;
  altText: string; credit: string; createdAt: string;
}
export interface TeamMember { userId: string; name: string; email: string; role: Role; status: 'active' | 'invited' | 'disabled' }
export interface AuditEntry { id: string; at: string; actor: string; action: string; resource: string; outcome: string }
export interface Settings {
  title: string; tagline: string; description: string;
  social: { facebook: string; twitter: string; instagram: string; youtube: string };
  hideViewCounts: boolean; pauseArchival: boolean;
}

export interface ArticleInput {
  id?: string;
  title: string;
  slug?: string;
  dek?: string;
  body: string;
  coverImageUrl?: string;
  categoryId: string;
  authorId: string;
  languageCode?: string;
  regionCode?: string;
  tags?: string[];
  status: ArticleStatus;
  scheduledFor?: string | null;
  seo?: Partial<SeoFields>;
}

/**
 * The signed-in person, as the SERVER knows them.
 *
 * `roles` and `permissions` come from the database, never from the client, so a
 * tampered bundle cannot widen its own access. `permissions` is the flattened
 * `role_permissions` set, which is what admin UI should gate on.
 */
export interface Me {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  permissions: string[];
}

export interface Repository {
  readonly kind: 'memory' | 'supabase' | 'http';
  /** Who the server says I am. `null` when signed out. */
  getMe(): Promise<Me | null>;
  listCategories(): Promise<Category[]>;
  getCategoryBySlug(slug: string): Promise<Category | null>;
  listAuthors(): Promise<Author[]>;
  /** Public listing: only content that is publicly visible right now. */
  listPublicArticles(opts?: { categoryId?: string; authorId?: string; query?: string; limit?: number; offset?: number }): Promise<Article[]>;
  getPublicArticleBySlug(slug: string): Promise<Article | null>;
  /** Admin listing: everything. */
  listAllArticles(opts?: { status?: ArticleStatus | 'all'; query?: string }): Promise<Article[]>;
  getArticleById(id: string): Promise<Article | null>;
  // Write methods no longer take an `actor` argument. Who is acting is decided
  // by the verified session token on the server, never by the caller — passing
  // an email here only ever fed the audit trail a claim it could not check.
  saveArticle(input: ArticleInput): Promise<Article>;
  deleteArticle(id: string): Promise<void>;
  listMedia(): Promise<MediaItem[]>;
  addMedia(item: Omit<MediaItem, 'id' | 'createdAt'>): Promise<MediaItem>;
  updateMedia(id: string, patch: { altText?: string; credit?: string }): Promise<MediaItem>;
  listTeam(): Promise<TeamMember[]>;
  inviteMember(email: string, role: Role): Promise<void>;
  removeMember(userId: string, role: Role): Promise<void>;
  saveCategory(input: { id?: string; name: string; slug?: string; description?: string; sortOrder?: number }): Promise<Category>;
  deleteCategory(id: string): Promise<void>;
  listAuditLog(): Promise<AuditEntry[]>;
  subscribe(email: string): Promise<{ ok: boolean; error?: string }>;
  getSettings(): Promise<Settings>;
  saveSettings(next: Settings): Promise<Settings>;
}

/** Shared, conservative email check (mirrors the DB constraint). */
export function isEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}

const nowIso = () => new Date().toISOString();
const uid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * The local demo identity. In demo/offline mode there is no server to ask, so
 * the in-memory adapter reports the seeded admin and the permission set that
 * mirrors the `role_permissions` seed in 0001_init.sql exactly.
 */
const DEMO_ME: Me = {
  id: 'demo-admin',
  email: 'kmec1983@gmail.com',
  name: 'Iqbal Chand',
  roles: ['admin'],
  permissions: [
    'articles.read',
    'articles.create',
    'articles.edit',
    'articles.review',
    'articles.publish',
    'articles.delete',
    'media.upload',
    'analytics.read',
    'users.manage',
    'social.manage',
    'settings.manage',
    'audit_logs.read',
  ],
};

/**
 * Audit label for writes made without a verified identity (offline demo mode).
 * Deliberately not a caller-supplied value: that is the whole point of the change.
 */
const UNVERIFIED_ACTOR = 'local';

/** Load the signed-in Supabase user id, for the actor columns. */
async function supabaseActorId(db: SupabaseClient): Promise<string | null> {
  try {
    const { data } = await db.auth.getUser();
    return data?.user?.id ?? null;
  } catch {
    return null;
  }
}

// Re-exported so existing callers (and the tests) keep importing it from here;
// the implementation lives in shared/slug.js so the server can use it too.
export { resolveSlug };

// --------------------------------------------------------------------------
// In-memory adapter
// --------------------------------------------------------------------------
export class MemoryRepository implements Repository {
  readonly kind = 'memory' as const;
  private articles: Article[];
  private categories: Category[];
  private authors: Author[];
  private media: MediaItem[];
  private team: TeamMember[];
  private audit: AuditEntry[];
  private settings: Settings;

  constructor() {
    this.categories = structuredClone(seed.CATEGORIES) as Category[];
    this.authors = structuredClone(seed.AUTHORS) as Author[];
    this.articles = structuredClone(seed.ARTICLES) as unknown as Article[];
    this.media = structuredClone(seed.MEDIA) as MediaItem[];
    this.team = structuredClone(seed.TEAM) as TeamMember[];
    this.audit = structuredClone(seed.AUDIT_LOG) as AuditEntry[];
    this.settings = structuredClone(seed.SITE_SETTINGS) as Settings;
  }

  private hydrate(a: Article | null): Article | null {
    if (!a) return null;
    return {
      ...a,
      category: this.categories.find((c) => c.id === a.categoryId) ?? null,
      author: this.authors.find((x) => x.id === a.authorId) ?? null,
    };
  }

  async listCategories() { return [...this.categories].sort((a, b) => a.sortOrder - b.sortOrder); }
  async getCategoryBySlug(slug: string) { return this.categories.find((c) => c.slug === slug) ?? null; }
  async listAuthors() { return [...this.authors]; }

  async getMe(): Promise<Me | null> { return structuredClone(DEMO_ME); }

  async listPublicArticles(opts: { categoryId?: string; authorId?: string; query?: string; limit?: number; offset?: number } = {}) {
    const visible = this.articles.filter((a) => isPubliclyVisible(a));
    return this.filterSort(visible, opts).map((a) => this.hydrate(a)!);
  }

  async getPublicArticleBySlug(slug: string) {
    const a = this.articles.find((x) => x.slug === slug && isPubliclyVisible(x));
    return this.hydrate(a ?? null);
  }

  async listAllArticles(opts: { status?: ArticleStatus | 'all'; query?: string } = {}) {
    let rows = this.articles.slice();
    if (opts.status && opts.status !== 'all') rows = rows.filter((a) => a.status === opts.status);
    return this.filterSort(rows, { query: opts.query }).map((a) => this.hydrate(a)!);
  }

  async getArticleById(id: string) { return this.hydrate(this.articles.find((a) => a.id === id) ?? null); }

  private filterSort(rows: Article[], opts: { categoryId?: string; authorId?: string; query?: string; limit?: number; offset?: number }) {
    let out = rows;
    if (opts.categoryId) out = out.filter((a) => a.categoryId === opts.categoryId);
    if (opts.authorId) out = out.filter((a) => a.authorId === opts.authorId);
    if (opts.query) {
      const q = opts.query.toLowerCase();
      out = out.filter((a) => a.title.toLowerCase().includes(q) || a.dek.toLowerCase().includes(q) || a.body.toLowerCase().includes(q));
    }
    out = [...out].sort(
      (a, b) =>
        new Date(b.publishedAt || b.scheduledFor || b.createdAt).getTime() -
        new Date(a.publishedAt || a.scheduledFor || a.createdAt).getTime(),
    );
    const offset = opts.offset ?? 0;
    return out.slice(offset, offset + (opts.limit ?? 100));
  }

  async saveArticle(input: ArticleInput): Promise<Article> {
    const taken = this.articles.filter((a) => a.id !== input.id).map((a) => a.slug);
    const slug = resolveSlug(input.title, input.slug, taken);
    const payload = {
      title: input.title,
      slug,
      dek: input.dek ?? '',
      body: input.body,
      coverImageUrl: input.coverImageUrl ?? '',
      categoryId: input.categoryId,
      authorId: input.authorId,
      languageCode: input.languageCode || 'en',
      regionCode: input.regionCode ?? '',
      tags: input.tags ?? [],
      status: input.status,
      scheduledFor: input.scheduledFor ?? null,
      seo: { title: '', description: '', canonical: '', ogImage: '', noindex: false, ...(input.seo || {}) },
    };
    const check = validate(articleSchema, payload);
    if (!check.ok) throw new ValidationError(check.errors);

    const existing = input.id ? this.articles.find((a) => a.id === input.id) : undefined;
    const statusChanged = !existing || existing.status !== payload.status;
    const article: Article = {
      id: existing?.id ?? uid('ar'),
      ...payload,
      publishedAt: payload.status === 'published' ? (existing?.publishedAt ?? nowIso()) : existing?.publishedAt ?? null,
      createdAt: existing?.createdAt ?? nowIso(),
      updatedAt: nowIso(),
      revision: (existing?.revision ?? 0) + 1,
      viewCount: existing?.viewCount ?? 0,
      category: null,
      author: null,
    };
    if (existing) this.articles = this.articles.map((a) => (a.id === article.id ? article : a));
    else this.articles = [article, ...this.articles];

    this.record(existing ? 'article.update' : 'article.create', article.title);
    if (statusChanged) this.record(`article.${payload.status}`, article.title);
    return this.hydrate(article)!;
  }

  async deleteArticle(id: string) {
    const a = this.articles.find((x) => x.id === id);
    this.articles = this.articles.filter((x) => x.id !== id);
    if (a) this.record('article.delete', a.title);
  }

  async listMedia() { return [...this.media].sort((a, b) => b.createdAt.localeCompare(a.createdAt)); }
  async addMedia(item: Omit<MediaItem, 'id' | 'createdAt'>) {
    const m: MediaItem = { ...item, id: uid('me'), createdAt: nowIso() };
    this.media = [m, ...this.media];
    this.record('media.upload', m.fileName);
    return m;
  }

  async updateMedia(id: string, patch: { altText?: string; credit?: string }) {
    let updated: MediaItem | null = null;
    this.media = this.media.map((m) => {
      if (m.id !== id) return m;
      updated = { ...m, altText: patch.altText ?? m.altText, credit: patch.credit ?? m.credit };
      return updated;
    });
    if (!updated) throw new Error('Media item not found');
    this.record('media.update', (updated as MediaItem).fileName);
    return updated;
  }

  async listTeam() { return [...this.team]; }
  async inviteMember(email: string, role: Role) {
    if (!isEmail(email)) throw new Error('Enter a valid email address.');
    this.team = [...this.team, { userId: uid('u'), name: email.split('@')[0] ?? email, email, role, status: 'invited' }];
    this.record('team.invite', `${email} (${role})`);
  }

  async removeMember(userId: string, role: Role) {
    const target = this.team.find((m) => m.userId === userId && m.role === role);
    if (!target) throw new Error('Team member not found');
    if (target.role === 'admin' && this.team.filter((m) => m.role === 'admin').length <= 1) {
      throw new Error('Cannot remove the last administrator');
    }
    this.team = this.team.filter((m) => !(m.userId === userId && m.role === role));
    this.record('role.revoke', `${target.email} (${role})`);
  }

  async saveCategory(input: { id?: string; name: string; slug?: string; description?: string; sortOrder?: number }) {
    const taken = this.categories.filter((c) => c.id !== input.id).map((c) => c.slug);
    const slug = resolveSlug(input.name, input.slug, taken);
    const check = validate(categorySchema, {
      name: input.name, slug, description: input.description ?? '', sortOrder: input.sortOrder ?? 0,
    });
    if (!check.ok) throw new ValidationError(check.errors);

    const existing = input.id ? this.categories.find((c) => c.id === input.id) : undefined;
    const category: Category = {
      id: existing?.id ?? uid('cat'),
      name: input.name.trim(),
      slug,
      description: input.description ?? '',
      sortOrder: input.sortOrder ?? existing?.sortOrder ?? 0,
    };
    this.categories = existing
      ? this.categories.map((c) => (c.id === category.id ? category : c))
      : [...this.categories, category];
    this.record(existing ? 'category.update' : 'category.create', category.name);
    return category;
  }

  async deleteCategory(id: string) {
    const inUse = this.articles.some((a) => a.categoryId === id);
    if (inUse) throw new Error('This category still has articles; move them first.');
    const cat = this.categories.find((c) => c.id === id);
    this.categories = this.categories.filter((c) => c.id !== id);
    if (cat) this.record('category.delete', cat.name);
  }

  async listAuditLog() { return [...this.audit].sort((a, b) => b.at.localeCompare(a.at)); }

  async subscribe(email: string) {
    if (!isEmail(email)) return { ok: false, error: 'Enter a valid email address.' };
    const key = 'tsl.newsletter';
    const list = new Set<string>(JSON.parse(localStorage.getItem(key) ?? '[]') as string[]);
    list.add(email.trim().toLowerCase());
    localStorage.setItem(key, JSON.stringify([...list]));
    return { ok: true };
  }

  async getSettings() { return structuredClone(this.settings); }
  async saveSettings(next: Settings) {
    this.settings = structuredClone(next);
    this.record('settings.update', 'site settings');
    return structuredClone(this.settings);
  }

  /** The in-memory adapter has no verified identity, so it labels writes honestly. */
  private record(action: string, resource: string) {
    this.audit = [{ id: uid('al'), at: nowIso(), actor: UNVERIFIED_ACTOR, action, resource, outcome: 'ok' }, ...this.audit];
  }
}

export class ValidationError extends Error {
  errors: Record<string, string>;
  constructor(errors: Record<string, string>) {
    super('Validation failed: ' + Object.values(errors).join('; '));
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

// --------------------------------------------------------------------------
// Supabase adapter
// --------------------------------------------------------------------------
interface Row {
  id: string; title: string; slug: string; dek: string; body: string; cover_image_url: string;
  category_id: string; author_id: string; language_code: string; region_code: string; tags: string[] | null;
  status: ArticleStatus; scheduled_for: string | null; published_at: string | null; created_at: string;
  updated_at: string; revision: number; view_count: number; seo_title: string; seo_description: string;
  og_image: string; canonical_url: string; noindex: boolean;
  categories?: { id: string; name: string; slug: string; description: string; sort_order: number } | null;
  authors?: { id: string; name: string; slug: string; bio: string; avatar_url: string } | null;
}

function rowToArticle(r: Row): Article {
  return {
    id: r.id, title: r.title, slug: r.slug, dek: r.dek, body: r.body, coverImageUrl: r.cover_image_url,
    categoryId: r.category_id, authorId: r.author_id, languageCode: r.language_code, regionCode: r.region_code,
    tags: r.tags ?? [], status: r.status, scheduledFor: r.scheduled_for, publishedAt: r.published_at,
    createdAt: r.created_at, updatedAt: r.updated_at, revision: r.revision, viewCount: r.view_count,
    seo: { title: r.seo_title, description: r.seo_description, canonical: r.canonical_url, ogImage: r.og_image, noindex: r.noindex },
    category: r.categories ? { id: r.categories.id, name: r.categories.name, slug: r.categories.slug, description: r.categories.description, sortOrder: r.categories.sort_order } : null,
    author: r.authors ? { id: r.authors.id, name: r.authors.name, slug: r.authors.slug, bio: r.authors.bio, avatarUrl: r.authors.avatar_url } : null,
  };
}

const ARTICLE_SELECT = '*, categories(id,name,slug,description,sort_order), authors(id,name,slug,bio,avatar_url)';

export class SupabaseRepository implements Repository {
  readonly kind = 'supabase' as const;
  constructor(private db: SupabaseClient) {}

  async listCategories(): Promise<Category[]> {
    const { data, error } = await this.db.from('categories').select('*').order('sort_order');
    if (error) throw error;
    return (data ?? []).map((c) => ({ id: c.id, name: c.name, slug: c.slug, description: c.description, sortOrder: c.sort_order }));
  }
  async getCategoryBySlug(slug: string) {
    const { data } = await this.db.from('categories').select('*').eq('slug', slug).maybeSingle();
    return data ? { id: data.id, name: data.name, slug: data.slug, description: data.description, sortOrder: data.sort_order } : null;
  }
  async listAuthors(): Promise<Author[]> {
    const { data } = await this.db.from('authors').select('*').order('name');
    return (data ?? []).map((a) => ({ id: a.id, name: a.name, slug: a.slug, bio: a.bio, avatarUrl: a.avatar_url }));
  }

  /**
   * Roles and permissions come from the database, never from the client, so a
   * tampered bundle cannot widen its own access. RLS is the real boundary; this
   * is what the UI is allowed to know about itself.
   */
  async getMe(): Promise<Me | null> {
    const { data } = await this.db.auth.getUser();
    const user = data?.user;
    if (!user) return null;

    const rolesQ = await this.db.from('user_roles').select('role').eq('user_id', user.id);
    const roles = ((rolesQ.data ?? []) as { role: Role }[]).map((r) => r.role);

    let permissions: string[] = [];
    try {
      const { data: perms, error } = await this.db.rpc('app_user_permissions', { p_user: user.id });
      if (!error && Array.isArray(perms)) permissions = perms as string[];
    } catch {
      // A missing helper should hide admin affordances, not break sign-in.
      permissions = [];
    }

    return {
      id: user.id,
      email: user.email ?? '',
      name: (user.user_metadata?.full_name as string | undefined) ?? user.email ?? '',
      roles,
      permissions,
    };
  }

  async listPublicArticles(opts: { categoryId?: string; authorId?: string; query?: string; limit?: number; offset?: number } = {}) {
    let q = this.db.from('articles').select(ARTICLE_SELECT).eq('status', 'published').order('published_at', { ascending: false });
    if (opts.categoryId) q = q.eq('category_id', opts.categoryId);
    if (opts.authorId) q = q.eq('author_id', opts.authorId);
    if (opts.query) q = q.textSearch('search_vector', opts.query, { type: 'websearch' });
    const { data, error } = await q.range(opts.offset ?? 0, (opts.offset ?? 0) + (opts.limit ?? 50) - 1);
    if (error) throw error;
    return (data as unknown as Row[]).map(rowToArticle);
  }

  async getPublicArticleBySlug(slug: string) {
    const { data } = await this.db.from('articles').select(ARTICLE_SELECT).eq('slug', slug).eq('status', 'published').maybeSingle();
    return data ? rowToArticle(data as unknown as Row) : null;
  }

  async listAllArticles(opts: { status?: ArticleStatus | 'all'; query?: string } = {}) {
    let q = this.db.from('articles').select(ARTICLE_SELECT).order('updated_at', { ascending: false });
    if (opts.status && opts.status !== 'all') q = q.eq('status', opts.status);
    if (opts.query) q = q.ilike('title', `%${opts.query}%`);
    const { data, error } = await q;
    if (error) throw error;
    return (data as unknown as Row[]).map(rowToArticle);
  }

  async getArticleById(id: string) {
    const { data } = await this.db.from('articles').select(ARTICLE_SELECT).eq('id', id).maybeSingle();
    return data ? rowToArticle(data as unknown as Row) : null;
  }

  async saveArticle(input: ArticleInput): Promise<Article> {
    const takenQ = await this.db.from('articles').select('slug').neq('id', input.id ?? '00000000-0000-0000-0000-000000000000');
    const taken = ((takenQ.data ?? []) as { slug: string }[]).map((r) => r.slug);
    const slug = resolveSlug(input.title, input.slug, taken);
    // The actor is the signed-in session, not an argument the caller supplies.
    const actorId = await supabaseActorId(this.db);
    const payload = {
      title: input.title, slug, dek: input.dek ?? '', body: input.body,
      cover_image_url: input.coverImageUrl ?? '', category_id: input.categoryId, author_id: input.authorId,
      language_code: input.languageCode || 'en', region_code: input.regionCode ?? '', tags: input.tags ?? [],
      status: input.status, scheduled_for: input.scheduledFor ?? null,
      seo_title: input.seo?.title ?? '', seo_description: input.seo?.description ?? '',
      og_image: input.seo?.ogImage ?? '', canonical_url: input.seo?.canonical ?? '', noindex: input.seo?.noindex ?? false,
      updated_by: actorId,
    };
    const check = validate(articleSchema, {
      ...payload,
      coverImageUrl: payload.cover_image_url, categoryId: payload.category_id, authorId: payload.author_id,
      languageCode: payload.language_code, regionCode: payload.region_code, scheduledFor: payload.scheduled_for,
      seo: { title: payload.seo_title, description: payload.seo_description, canonical: payload.canonical_url, ogImage: payload.og_image, noindex: payload.noindex },
    });
    if (!check.ok) throw new ValidationError(check.errors);

    const res = input.id
      ? await this.db.from('articles').update(payload).eq('id', input.id).select(ARTICLE_SELECT).single()
      : await this.db.from('articles').insert({ ...payload, created_by: actorId }).select(ARTICLE_SELECT).single();
    if (res.error) throw res.error;
    return rowToArticle(res.data as unknown as Row);
  }

  async deleteArticle(id: string) {
    const { error } = await this.db.from('articles').delete().eq('id', id);
    if (error) throw error;
  }

  async listMedia(): Promise<MediaItem[]> {
    const { data } = await this.db.from('media').select('*').order('created_at', { ascending: false });
    return (data ?? []).map((m) => ({ id: m.id, fileName: m.file_name, url: m.url, mimeType: m.mime_type, sizeBytes: m.size_bytes, altText: m.alt_text, credit: m.credit, createdAt: m.created_at }));
  }
  async addMedia(item: Omit<MediaItem, 'id' | 'createdAt'>) {
    const { data, error } = await this.db.from('media').insert({ file_name: item.fileName, url: item.url, mime_type: item.mimeType, size_bytes: item.sizeBytes, alt_text: item.altText, credit: item.credit }).select().single();
    if (error) throw error;
    return { id: data.id, fileName: data.file_name, url: data.url, mimeType: data.mime_type, sizeBytes: data.size_bytes, altText: data.alt_text, credit: data.credit, createdAt: data.created_at };
  }

  async listTeam(): Promise<TeamMember[]> {
    const { data } = await this.db.from('user_roles').select('role, profiles(user_id,display_name)');
    return (data ?? []).map((r: any) => ({ userId: r.profiles?.user_id ?? '', name: r.profiles?.display_name ?? 'Unknown', email: '', role: r.role, status: 'active' as const }));
  }
  async inviteMember(email: string, role: Role) {
    const { error } = await this.db.from('admin_invites').insert({ email, role });
    if (error) throw error;
  }

  async updateMedia(id: string, patch: { altText?: string; credit?: string }) {
    const { data, error } = await this.db
      .from('media')
      .update({ alt_text: patch.altText, credit: patch.credit })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { id: data.id, fileName: data.file_name, url: data.url, mimeType: data.mime_type, sizeBytes: data.size_bytes, altText: data.alt_text, credit: data.credit, createdAt: data.created_at };
  }

  async removeMember(userId: string, role: Role) {
    if (role === 'admin') {
      const { count } = await this.db.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'admin');
      if ((count ?? 0) <= 1) throw new Error('Cannot remove the last administrator');
    }
    const { error } = await this.db.from('user_roles').delete().eq('user_id', userId).eq('role', role);
    if (error) throw error;
  }

  async saveCategory(input: { id?: string; name: string; slug?: string; description?: string; sortOrder?: number }) {
    const takenQ = await this.db
      .from('categories')
      .select('slug')
      .neq('id', input.id ?? '00000000-0000-0000-0000-000000000000');
    const taken = ((takenQ.data ?? []) as { slug: string }[]).map((r) => r.slug);
    const slug = resolveSlug(input.name, input.slug, taken);
    const check = validate(categorySchema, {
      name: input.name, slug, description: input.description ?? '', sortOrder: input.sortOrder ?? 0,
    });
    if (!check.ok) throw new ValidationError(check.errors);

    const payload = { name: input.name, slug, description: input.description ?? '', sort_order: input.sortOrder ?? 0 };
    const res = input.id
      ? await this.db.from('categories').update(payload).eq('id', input.id).select().single()
      : await this.db.from('categories').insert(payload).select().single();
    if (res.error) throw res.error;
    const c = res.data;
    return { id: c.id, name: c.name, slug: c.slug, description: c.description, sortOrder: c.sort_order };
  }

  async deleteCategory(id: string) {
    const { count } = await this.db.from('articles').select('*', { count: 'exact', head: true }).eq('category_id', id);
    if ((count ?? 0) > 0) throw new Error('This category still has articles; move them first.');
    const { error } = await this.db.from('categories').delete().eq('id', id);
    if (error) throw error;
  }

  async listAuditLog(): Promise<AuditEntry[]> {
    const { data } = await this.db.from('audit_log').select('*').order('at', { ascending: false }).limit(200);
    return (data ?? []).map((a) => ({ id: String(a.id), at: a.at, actor: a.actor_label, action: a.action, resource: a.summary || a.resource_id, outcome: a.outcome }));
  }

  async subscribe(email: string) {
    if (!isEmail(email)) return { ok: false, error: 'Enter a valid email address.' };
    const { error } = await this.db.from('newsletter_subscribers').insert({ email: email.trim().toLowerCase(), source: 'home' });
    if (error && error.code !== '23505') return { ok: false, error: 'Could not subscribe right now.' };
    return { ok: true };
  }

  async getSettings(): Promise<Settings> {
    const { data } = await this.db.from('site_settings').select('key,value');
    const map = Object.fromEntries((data ?? []).map((r) => [r.key, r.value])) as Record<string, any>;
    return {
      title: map.site?.title ?? 'The Story Line',
      tagline: map.site?.tagline ?? '',
      description: map.site?.description ?? '',
      social: { facebook: map.social?.facebook ?? '', twitter: map.social?.twitter ?? '', instagram: map.social?.instagram ?? '', youtube: map.social?.youtube ?? '' },
      hideViewCounts: !!map.display?.hideViewCounts,
      pauseArchival: !!map.lifecycle?.pauseArchival,
    };
  }
  async saveSettings(next: Settings) {
    await this.db.from('site_settings').upsert([
      { key: 'site', value: { title: next.title, tagline: next.tagline, description: next.description } },
      { key: 'social', value: next.social },
      { key: 'display', value: { hideViewCounts: next.hideViewCounts } },
      { key: 'lifecycle', value: { pauseArchival: next.pauseArchival } },
    ]);
    return next;
  }
}

// --------------------------------------------------------------------------
// Selection
// --------------------------------------------------------------------------
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY as string | undefined;
/** Set VITE_API_MODE=http to run the SPA against the local Postgres via /api. */
const API_MODE = import.meta.env?.VITE_API_MODE as string | undefined;

export const supabase: SupabaseClient | null =
  SUPABASE_URL && SUPABASE_ANON_KEY ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY) : null;

let repo: Repository | null = null;
export function getRepo(): Repository {
  if (!repo) {
    if (supabase) repo = new SupabaseRepository(supabase);
    else if (API_MODE === 'http') repo = new HttpRepository();
    else repo = new MemoryRepository();
  }
  return repo;
}

export const isDemoMode = !supabase && API_MODE !== 'http';

